export const LOCALIZED_LOCALES = ['en-bd', 'en-pk', 'en-eg'] as const;

export type LocalizedLocale = (typeof LOCALIZED_LOCALES)[number];

/**
 * Canonical paths that were published through Webflow Localization and must
 * continue to resolve after the Payload migration.
 *
 * Source: apps/website/nsave-urls.csv (31 rows, supplied 2026-07-31).
 */
export const LOCALIZED_CANONICAL_PATHS = [
  '/',
  '/about',
  '/article/how-nsave-keeps-your-money-safe',
  '/article/how-to-deposit-crypto-to-your-nsave-account-in-app',
  '/article/how-to-earn-up-to-4-2-yearly-rewards-with-nsave',
  '/article/how-to-invest-in-funds-and-etfs-with-nsave-for-people-from-pakistan',
  '/article/how-to-send-money-from-payoneer-to-nsave',
  '/article/how-to-send-money-to-syria-with-nsave',
  '/article/how-to-withdraw-in-stablecoin-from-your-nsave-account',
  '/article/if-you-get-paid-in-euros-from-egypt-this-is-the-account-youve-been-waiting-for',
  '/bangladesh/sonali-bank-routing-number',
  '/calculator/usd-bdt',
  '/calculator/usd-egp',
  '/calculator/usd-pkr',
  '/calculator/usd-try',
  '/careers',
  '/compare',
  '/pakistan/usd-account',
  '/perks',
  '/pricing',
  '/terms/account/corporate',
  '/terms/application',
  '/terms/card-funding-terms-conditions',
  '/terms/card-terms',
  '/terms/cookie',
  '/terms/fees',
  '/terms/general',
  '/terms/membership-agreement',
  '/terms/rewards',
  '/terms/usd-wallet-terms',
  '/zakat-calculator',
] as const;

const localizedCanonicalPaths = new Set<string>(LOCALIZED_CANONICAL_PATHS);
const localizedLocales = new Set<string>(LOCALIZED_LOCALES);

export function isLocalizedLocale(value: string): value is LocalizedLocale {
  return localizedLocales.has(value.toLowerCase());
}

export function normalizeCanonicalPath(path: string): string {
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  if (withLeadingSlash === '/') return withLeadingSlash;
  return withLeadingSlash.replace(/\/+$/u, '');
}

export function isLocalizedCanonicalPath(path: string): boolean {
  return localizedCanonicalPaths.has(normalizeCanonicalPath(path));
}

export function localizedPath(path: string, locale: LocalizedLocale): string {
  const canonicalPath = normalizeCanonicalPath(path);
  return canonicalPath === '/' ? `/${locale}` : `/${locale}${canonicalPath}`;
}

export function localizedLocaleFromPathname(pathname: string): LocalizedLocale | undefined {
  const [locale] = pathname.replace(/^\/+/, '').split('/');
  return locale && isLocalizedLocale(locale) ? locale : undefined;
}

export function htmlLanguageForPathname(pathname: string): string {
  const locale = localizedLocaleFromPathname(pathname);
  if (!locale) return 'en';
  const [language, region] = locale.split('-');
  return `${language}-${region.toUpperCase()}`;
}

export interface HreflangLink {
  rel: 'alternate';
  hrefLang: 'en' | LocalizedLocale | 'x-default';
  href: string;
}

export function localizedAlternateLinks(
  path: string,
  absoluteUrl: (path: string) => string,
): HreflangLink[] {
  const canonicalPath = normalizeCanonicalPath(path);
  if (!isLocalizedCanonicalPath(canonicalPath)) return [];

  const englishUrl = absoluteUrl(canonicalPath);
  return [
    { rel: 'alternate', hrefLang: 'en', href: englishUrl },
    ...LOCALIZED_LOCALES.map((locale) => ({
      rel: 'alternate' as const,
      hrefLang: locale,
      href: absoluteUrl(localizedPath(canonicalPath, locale)),
    })),
    { rel: 'alternate', hrefLang: 'x-default', href: englishUrl },
  ];
}
